import { ClbModal } from '@celebration/react';
import { useTranslation } from 'react-i18next';
import { ItemsFooterType } from '@celebration/react/ClbModal/ClbModal.types';
import { useState, useCallback } from 'react';
import ILoadsBulkActionConfirmationProps from './interfaces/ILoadsBulkActionConfirmationProps';
import LoadsBulkAction from '../../enums/loads-bulk-action-type.enum';
import LoadStatus from '../../../../../common/enums/loads/load-status.enum';
import './loads-bulk-action-confirmation.scss';
import { useToast } from '../../../../../app/contexts/toast/toast.context';
import LoadsConstants from '../../constants';
import LoadCardReOfferModal from '../load-card/components/load-card-reoffer-modal';
import LoadCardSuspensionModal from '../load-card/components/load-card-suspension-modal';
import LoadManager from '../../../../../common/services/loadmanager';
import { isPastLoad } from '../../utils/loads-utils';
import ILoadSuspensionRequest from '../../../../../common/interfaces/loads/ILoadSuspensionRequest';
import ILoadCardInfo from '../../../../../common/interfaces/loads/ILoadCardInfo';

function LoadsBulkActionConfirmation({
    action,
    selectedLoads,
    onConfirm,
    onCancel,
}: Readonly<ILoadsBulkActionConfirmationProps>) {
    const { t } = useTranslation();
    const { setToast } = useToast();
    const statusDuringRounds = [
        LoadStatus.Ready,
        LoadStatus.WaitingAcceptance,
        LoadStatus.WaitingConfirmation,
    ];
    const statusToSuspend = [...statusDuringRounds, LoadStatus.Confirmed];

    const [isShowingReofferModal, setIsShowingReofferModal] = useState(false);
    const [isShowingSuspensionModal, setIsShowingSuspensionModal] =
        useState(false);
    const [suspensionReason, setSuspensionReason] = useState<string>('');

    const elegibleLoads = selectedLoads.filter(load => isElegible(load));
    const inelegibleLoads = selectedLoads.filter(load => !isElegible(load));

    const handleLoadServiceResult = useCallback(
        (
            data: any,
            errorTitle: string,
            errorMessage: string,
            successTitle: string,
        ) => {
            if (data?.loadsIdWithError?.length) {
                const params = new URLSearchParams();
                data.loadsIdWithError.map((id: string) =>
                    params.append(LoadsConstants.LOAD_ID_KEY, id),
                );

                const redirectParams = `${LoadsConstants.PATH}?${params}`;

                setToast('warning', errorTitle, errorMessage, {
                    title: t('TryAgain'),
                    href: redirectParams,
                });
            } else {
                setToast('positive', successTitle);
            }
        },
        [setToast, t],
    );

    const handleException = useCallback(
        (title: string, message: string) => {
            const params = new URLSearchParams();
            elegibleLoads.map(l =>
                params.append(LoadsConstants.LOAD_ID_KEY, l.idLoad),
            );
            const redirectParams = `${LoadsConstants.PATH}?${params}`;

            setToast('negative', title, message, {
                title: t('TryAgain'),
                href: redirectParams,
            });
        },
        [setToast, t, elegibleLoads],
    );

    const onCancelClick = (): void => {
        setIsShowingReofferModal(false);
        setIsShowingSuspensionModal(false);
        setSuspensionReason('');
        onCancel();
    };

    const reofferLoads = useCallback(async () => {
        const { response, error } = await LoadManager.reOfferLoads(
            elegibleLoads.map(x => x.idLoad),
        );

        if (error !== undefined) {
            handleException(t('Loads.ReofferWithError'), error.message);
        }

        if (response !== undefined) {
            handleLoadServiceResult(
                response,
                t('Loads.ReofferWithWarningTitle'),
                t('Loads.ReofferWithWarningDescription', {
                    loadIds: response.loadsIdWithError,
                }),
                t('Loads.ReofferWithSuccessTitle'),
            );
        }

        setIsShowingReofferModal(false);
        onConfirm();
    }, [t, elegibleLoads, handleException, handleLoadServiceResult, onConfirm]);

    const suspendLoads = useCallback(async () => {
        const request: ILoadSuspensionRequest[] = elegibleLoads.map(x => {
            return {
                loadId: x.idLoad,
                reason: suspensionReason,
            } as ILoadSuspensionRequest;
        });

        await LoadManager.suspendLoads(request)
            .then((data: any) => {
                handleLoadServiceResult(
                    data,
                    t('Loads.SuspendWithWarningTitle'),
                    t('Loads.SuspendWithWarningDescription', {
                        loadIds: data.loadsIdWithError,
                    }),
                    t('Loads.SuspendWithSuccessTitle'),
                );
            })
            .catch((error: Error) => {
                handleException(t('Loads.SuspendWithError'), error.message);
            })
            .finally(() => {
                setSuspensionReason('');
                setIsShowingSuspensionModal(false);
                onConfirm();
            });
    }, [
        t,
        elegibleLoads,
        handleException,
        handleLoadServiceResult,
        onConfirm,
        suspensionReason,
    ]);

    function isElegibleToSuspension(load: ILoadCardInfo): boolean {
        return statusToSuspend.includes(load.tagStatus);
    }

    function isElegibleToReoffer(load: ILoadCardInfo): boolean {
        return (
            (load.tagStatus === LoadStatus.Suspended ||
                load.tagStatus === LoadStatus.CancelledTms) &&
            !isPastLoad(load.routeDetails?.collectionDate)
        );
    }

    function isElegibleToPrioritize(load: ILoadCardInfo): boolean {
        return (
            statusDuringRounds.includes(load.tagStatus) &&
            !load.prioritized &&
            !isPastLoad(load.routeDetails?.collectionDate)
        );
    }

    function isElegible(load: ILoadCardInfo): boolean {
        switch (action) {
            case LoadsBulkAction.Suspend:
                return isElegibleToSuspension(load);
            case LoadsBulkAction.Reoffer:
                return isElegibleToReoffer(load);
            default:
                return isElegibleToPrioritize(load);
        }
    }

    function getActionLabel(): string {
        switch (action) {
            case LoadsBulkAction.Suspend:
                return t('Suspender');
            case LoadsBulkAction.Reoffer:
                return t('Reofertar');
            default:
                return t('Priorizar');
        }
    }

    const prioritizeLoad = useCallback(async () => {
        await LoadManager.prioritizeLoads(
            elegibleLoads.map(l => l.idLoad),
        ).then(({ response: data, error }) => {
            if (error !== undefined) {
                handleException(t('Loads.PrioritizeWithError'), error.message);
            }

            if (data !== undefined) {
                handleLoadServiceResult(
                    data,
                    t('Loads.PrioritizeWithWarningTitle'),
                    t('Loads.PrioritizeWithWarningDescription', {
                        loadIds: data.loadsIdWithError,
                    }),
                    t('Loads.PrioritizeWithSuccessTitle'),
                );
            }

            onConfirm();
        });
    }, [elegibleLoads, onConfirm, t, handleException, handleLoadServiceResult]);

    const onConfirmClick = useCallback(() => {
        (async () => {
            switch (action) {
                case LoadsBulkAction.Prioritize:
                    await prioritizeLoad();
                    break;
                case LoadsBulkAction.Suspend:
                    setIsShowingSuspensionModal(true);
                    break;
                default:
                    setIsShowingReofferModal(true);
                    break;
            }
        })();
    }, [action, prioritizeLoad]);

    function getActionTitles() {
        switch (action) {
            case LoadsBulkAction.Suspend:
                return {
                    elegible: t('Loads.ElegiveisParaSuspensao'),
                    inelegible: t('Loads.NaoElegiveisParaSuspensao'),
                };
            case LoadsBulkAction.Reoffer:
                return {
                    elegible: t('Loads.ElegiveisParaReoferta'),
                    inelegible: t('Loads.NaoElegiveisParaReoferta'),
                };
            default:
                return {
                    elegible: t('Loads.ElegiveisParaPriorizacao'),
                    inelegible: t('Loads.NaoElegiveisParaPriorizacao'),
                };
        }
    }

    function getBulkActionInfo() {
        const actionTitles = getActionTitles();

        return (
            <div
                id="load-bulk-action-confirmation-message"
                data-testid="load-bulk-action-confirmation-message"
                className="modal-confirmation-body"
            >
                <div
                    className="modal-confirmation-subtitle"
                    data-testid="load-bulk-action-confirmation-message-title"
                >
                    {t('Loads.TituloCargasSelecionadas')}
                </div>
                {elegibleLoads.length > 0 && (
                    <div data-testid="load-bulk-action-confirmation-message-elegible">
                        <span className="modal-confirmation-section">
                            {actionTitles.elegible}
                        </span>
                        <div>
                            {elegibleLoads.map(load => load.idLoad).join(', ')}
                        </div>
                    </div>
                )}
                {inelegibleLoads.length > 0 && (
                    <div data-testid="load-bulk-action-confirmation-message-inelegible">
                        <span className="modal-confirmation-section">
                            {actionTitles.inelegible}
                        </span>
                        <div>
                            {inelegibleLoads
                                .map(load => load.idLoad)
                                .join(', ')}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    function getButtons(): ItemsFooterType {
        return [
            {
                id: 'loads-bulk-action-confirmation-modal-proceed',
                key: 'loads-bulk-action-confirmation-modal-proceed',
                'data-testid': 'loads-bulk-action-confirmation-modal-proceed',
                type: 'button',
                styleType: 'danger',
                role: 'button',
                label: t('Prosseguir'),
                title: t('UsuarioConfirmacaoAcaoSuspensao'),
                icon: 'spam',
                loading: false,
                onClick: onConfirmClick,
                disabled: elegibleLoads?.length === 0,
            },
            {
                id: 'loads-bulk-action-confirmation-modal-cancel',
                key: 'loads-bulk-action-confirmation-modal-cancel',
                'data-testid': 'loads-bulk-action-confirmation-modal-cancel',
                type: 'button',
                styleType: 'secondary',
                role: 'button',
                label: t('Cancelar'),
                title: t('UsuarioCancelaAcaoSuspensao'),
                disabled: false,
                loading: false,
                onClick: onCancelClick,
            },
        ];
    }

    function getReofferModal() {
        return (
            <LoadCardReOfferModal
                reOfferModalOpen={isShowingReofferModal}
                reOfferModalOnClose={onCancelClick}
                reOfferModalOnCancel={onCancelClick}
                reOfferModalOnReOffer={reofferLoads}
            />
        );
    }

    function getSuspensionModal() {
        return (
            <LoadCardSuspensionModal
                onSubmit={suspendLoads}
                onCancel={onCancelClick}
                onChangeReason={setSuspensionReason}
            />
        );
    }

    return (
        <>
            <ClbModal
                key="loads-bulk-action-confirmation-modal"
                title={getActionLabel()}
                open
                onClose={onCancel}
                itemsFooter={getButtons()}
            >
                {getBulkActionInfo()}
            </ClbModal>
            {isShowingReofferModal && getReofferModal()}
            {isShowingSuspensionModal && getSuspensionModal()}
        </>
    );
}

export default LoadsBulkActionConfirmation;
